import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Calendar,
  User,
  Shield,
  Eye,
  ArrowRight
} from 'lucide-react';
import { contractAccessService } from '../../services';
import { get } from 'react-hook-form';

const ClientContractsDashboard = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [expandedYears, setExpandedYears] = useState({});

  // Încarcă toate contractele clientului
  const { data, isLoading, error } = useQuery(
    ['clientContracts', token],
    () => contractAccessService.getClientContracts(token),
    {
      enabled: !!token,
      onError: (error) => {
        console.error('Error loading contracts:', error);
        toast.error('Eroare la încărcarea contractelor');
      }
    }
  );

  // Grupează contractele pe ani
  const groupContractsByYear = (contracts) => {
    if (!contracts) return {};
    
    const grouped = contracts.reduce((acc, contract) => {
      const year = contract.year;
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(contract);
      return acc;
    }, {});

    // Sortează anii descrescător
    return Object.keys(grouped)
      .sort((a, b) => b - a)
      .reduce((acc, year) => {
        acc[year] = grouped[year];
        return acc;
      }, {});
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  // ✨ NOU - Navigare către DocumentViewerPage
  const handleViewContract = (contract) => {
    // Token-ul rămâne același - backend-ul știe că e autentificat
    // Navigăm către pagina de detalii a contractului
    navigate(`/contract-access/${token}/view`, {
      state: { 
        contractId: contract.id,
        contractNumber: contract.number,
        fromDashboard: true // ✨ Flag pentru butonul "Înapoi"
      }
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Draft: { bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock, label: 'Ciornă' },
      Generated: { bg: 'bg-blue-100', text: 'text-blue-800', icon: FileText, label: 'Generat' },
      Sent: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Trimis' },
      Accessed: { bg: 'bg-purple-100', text: 'text-purple-800', icon: Clock, label: 'Accesat' },
      Signed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Semnat' },
      Expired: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle, label: 'Expirat' },
      Terminated: { bg: 'bg-orange-100', text: 'text-orange-800', icon: AlertCircle, label: 'Reziliat' }
    };
    
    const config = statusConfig[Object.keys(statusConfig)[status]] || statusConfig.Draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă contractele...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Eroare</h2>
          <p className="text-gray-600">Nu s-au putut încărca contractele</p>
        </div>
      </div>
    );
  }

  const groupedContracts = groupContractsByYear(data.contracts);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Shield className="w-8 h-8 text-blue-500 mr-3" />
                Contractele Mele
              </h1>
              <p className="mt-1 text-sm text-gray-600 flex items-center">
                <User className="w-4 h-4 mr-1" />
                {data.clientName}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Total contracte</p>
              <p className="text-2xl font-bold text-blue-600">{data.totalContracts}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contracts List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {Object.entries(groupedContracts).length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nu există contracte disponibile</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedContracts).map(([year, contracts]) => (
              <div key={year} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Year Header */}
                <button
                  onClick={() => toggleYear(year)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <span className="text-lg font-semibold text-gray-900">Anul {year}</span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {contracts.length} {contracts.length === 1 ? 'contract' : 'contracte'}
                    </span>
                  </div>
                  {expandedYears[year] ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                {/* Contracts for Year */}
                {expandedYears[year] && (
                  <div className="border-t border-gray-200">
                    {contracts.map((contract, index) => (
                      <div
                        key={contract.id}
                        className={`px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                          index !== 0 ? 'border-t border-gray-100' : ''
                        }`}
                        onClick={() => handleViewContract(contract)}
                      >
                        <div className="flex items-start justify-between flex-wrap gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3 mb-2 flex-wrap">
                              <h3 className="text-base font-medium text-gray-900">
                                Contract {contract.number}
                              </h3>
                              {console.log(getStatusBadge(contract.status))}
                              {getStatusBadge(contract.status)}
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>
                                Perioadă: {new Date(contract.startDate).toLocaleDateString('ro-RO')} 
                                {' - '}
                                {new Date(contract.dueDate).toLocaleDateString('ro-RO')}
                              </p>
                              <div className="flex items-center gap-4 text-xs">
                                {contract.additionalActs?.length > 0 && (
                                  <span className="flex items-center">
                                    <FileText className="w-3 h-3 mr-1 text-blue-500" />
                                    {contract.additionalActs.length} acte adiționale
                                  </span>
                                )}
                                {contract.notices?.length > 0 && (
                                  <span className="flex items-center">
                                    <AlertCircle className="w-3 h-3 mr-1 text-red-500" />
                                    {contract.notices.length} notificări
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* View Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewContract(contract);
                            }}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Vezi detalii
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Navigare rapidă
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Dă click pe orice contract pentru a vedea detaliile complete, 
                  pentru a descărca PDF-ul sau pentru a încărca documentele semnate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientContractsDashboard;